import { Button, Flex, Spacer, Text } from "@chakra-ui/react";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { RanaLogoIcon, RanaModal, RanaTable } from "@nens/ranalib";
import { Template } from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { APP_BASE_HREF } from "../App";
import Snackbar from "../components/Snackbar";
import { startWithTemplate } from "../state/appState";

import heightWidthStyles from "./../styles/HeightWidth.module.css";
import StartScreenBackground from "./StartScreenBackground";
// Reuse styles from the start screen

interface ChooseSimulationTemplateScreenProps {
  modelId: number;
  organisationId: string;
  simulationTemplates: Template[];
  count: number;
}

const TEMPLATES_PER_PAGE = 5;

const ChooseSimulationTemplateScreen: FC<ChooseSimulationTemplateScreenProps> = ({
  modelId,
  organisationId,
  simulationTemplates,
  count,
}) => {
  const [page, setPage] = useState<number>(1);
  const [templatesOnPage, setTemplatesOnPage] = useState<Template[]>(
    simulationTemplates.slice(0, TEMPLATES_PER_PAGE)
  );

  const changePage = useCallback(
    (page: number) => {
      setPage(page);
      (async () => {
        const v3Api = getV3Api();
        const templatesResponse = await v3Api.simulationTemplatesList({
          simulationThreedimodelId: modelId,
          offset: (page - 1) * TEMPLATES_PER_PAGE,
          limit: TEMPLATES_PER_PAGE,
        });
        setTemplatesOnPage(templatesResponse.results);
      })();
    },
    [modelId]
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [chosenTemplate, setChosenTemplate] = useState<Template | null>(null);

  const start = useCallback(() => {
    if (chosenTemplate) {
      startWithTemplate(modelId, "" + organisationId, chosenTemplate.id!, dispatch).then(() =>
        navigate(`${APP_BASE_HREF}/`)
      );
    }
  }, [modelId, organisationId, chosenTemplate, dispatch, navigate]);

  const totalPages = Math.max(1 + Math.floor((count - 1) / TEMPLATES_PER_PAGE), 0);

  return (
    <div className={heightWidthStyles.Full}>
      <StartScreenBackground>
        <Snackbar />
        <RanaModal
          isOpen={true}
          minWidth="1200px"
          minHeight="600px"
          topBar={
            <Flex w="100%" gap={1}>
              <RanaLogoIcon color="brand.clear_creek" width={24} />
              <Text fontSize="150%">
                Rana Live &mdash;{" "}
                <FormattedMessage
                  id="select_template.header"
                  defaultMessage={"Select simulation template"}
                />
              </Text>
              <Spacer />
            </Flex>
          }
          buttons={[
            <Button variant="primary" onClick={start} isDisabled={chosenTemplate === null}>
              <FormattedMessage id="select_model.button_start" defaultMessage="START" />
            </Button>,
          ]}
        >
          <p>This Rana HCC model has several simulation templates configured. Please select one.</p>
          <RanaTable
            rows={5}
            columnDefinitions={[
              {
                width: "40%",
                title: intl.formatMessage({
                  id: "select_model.table_header_name",
                  defaultMessage: "Name",
                }),
              },
              {
                width: "30%",
                title: "Template ID",
              },
              { width: "30%", title: "Creation date" },
            ]}
            tableDataIds={templatesOnPage.map((template) => "" + template.id)}
            emptyElement={<Text>No simulation templates to show</Text>}
          >
            <RanaTable.Container minH={(44 + 2) * 6}>
              <RanaTable.Header />
              {templatesOnPage.map((template) => (
                <RanaTable.Row
                  key={"" + template.id}
                  rowId={"" + template.id}
                  onClick={() => setChosenTemplate(template)}
                  isSelected={!!chosenTemplate && chosenTemplate.id === template.id}
                >
                  <RanaTable.Cell>{template.name}</RanaTable.Cell>
                  <RanaTable.Cell>{template.id}</RanaTable.Cell>
                  <RanaTable.Cell>
                    {template.created ? new Date(template.created).toLocaleDateString() : "---"}
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Container>
            <RanaTable.Pagination
              limit={5}
              page={page}
              numPages={totalPages}
              setPage={changePage}
            />
          </RanaTable>
        </RanaModal>
      </StartScreenBackground>
    </div>
  );
};

export default ChooseSimulationTemplateScreen;
